import React, {useEffect, useRef} from 'react';
import {Avatar, Box, CircularProgress, Paper, Typography,} from '@mui/material';
import {Message} from './Message';
import MithraIcon from '../../../../public/chat/MithraSmallLogo.svg?react';
import {useChatContext} from "../context/ChatContext";
import {ChatUserInput} from "./ChatUserInput.tsx";
import ChatConstants from "../ChatConstants.ts";

export const ChatArea: React.FC = () => {
    const {
        currentSession,
        hasReceivedFinalMessage,
        isRestoringSession,
    } = useChatContext();

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
    }, [currentSession?.messages]);

    // Focus input when appropriate
    useEffect(() => {
        // Focus on initial load or when switching to empty session
        if (!currentSession?.messages.length) {
            inputRef.current?.focus();
        }
        // Focus when receiving final message and not waiting for response
        else if (hasReceivedFinalMessage) {
            inputRef.current?.focus();
        }
    }, [hasReceivedFinalMessage, currentSession?.messages.length]);

    return (
        <Box className="chat-area chat-area-container">
            {(currentSession || isRestoringSession) ? (
                <>
                    {/* Chat Header */}
                    <Paper
                        elevation={0}
                        className="chat-header"
                    >
                        {/*<Typography variant="h2" sx={{fontSize: '1.125rem', fontWeight: 600}}>*/}
                        {/*    {currentSession.name}*/}
                        {/*</Typography>*/}
                    </Paper>

                    {/* Messages Container */}
                    <Box className="messages-container">
                        {!currentSession && isRestoringSession && (
                            <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2}}>
                                <CircularProgress size={20}/>
                            </Box>
                        )}
                        {currentSession?.messages.map((message, index) => {
                            return (
                                <Message
                                    key={message.id}
                                    message={message}
                                />
                            );
                        })}
                        <div ref={messagesEndRef}/>
                        {!hasReceivedFinalMessage && (
                            <Box sx={{display: 'flex', gap: 2, mb: 4}}>
                                <Avatar className="chat-avatar bot-avatar">
                                    <MithraIcon
                                        style={{
                                            width: 45,
                                            height: 45,
                                        }}
                                    />
                                </Avatar>
                                <Paper
                                    sx={{
                                        p: 2,
                                        backgroundColor: 'background.paper',
                                        borderRadius: 2,
                                        minWidth: 120,
                                    }}
                                >
                                    <Box className="typing-indicator">
                                        {[0, 1, 2].map((i) => (
                                            <Box
                                                key={i}
                                                className="dot"
                                                sx={{
                                                    animationDelay: `${i * 0.2}s`,
                                                    // Fallback styles in case SCSS doesn't load
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    backgroundColor: '#666',
                                                    animation: 'typingPulse 1.4s ease-in-out infinite',
                                                    '@keyframes typingPulse': {
                                                        '0%, 60%, 100%': {
                                                            transform: 'scale(1)',
                                                            opacity: 0.5,
                                                        },
                                                        '30%': {
                                                            transform: 'scale(1.2)',
                                                            opacity: 1,
                                                        },
                                                    },
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Paper>
                            </Box>
                        )}
                    </Box>
                </>
            ) : (
                /* Welcome Screen */
                <Box
                    className="welcome-screen"
                    sx={{
                        background: 'radial-gradient(ellipse at center, rgba(0,29,108,0.03) 0%, rgba(255,255,255,0.01) 70%)',
                        '&::before': {
                            background: 'radial-gradient(ellipse, rgba(0,29,108,0.02) 0%, transparent 70%)',
                        },
                    }}
                >
                    <Paper
                        className="welcome-card"
                        sx={{
                            backgroundColor: 'rgba(255,255,255,0.8)',
                            border: '1px solid rgba(0,0,0,0.08)',
                        }}
                    >
                        <Typography
                            variant="h1"
                            className="welcome-title"
                        >
                            I'm {ChatConstants.name}, your AI procurement copilot
                        </Typography>
                        <Typography
                            variant="h5"
                            className="welcome-subtitle"
                            sx={{
                                color: 'text.secondary',
                            }}
                        >
                            Ask questions, explore your spend, or request insights
                        </Typography>
                        <Typography
                            variant="body1"
                            className="welcome-description"
                            sx={{
                                color: 'text.secondary',
                            }}
                        >
                            {ChatConstants.name} analyzes your data in real time to help you make faster, better
                            decisions
                        </Typography>
                        {/*{authStore.isMithraStaff && <Typography variant="body1" sx={{color: 'text.secondary'}}>*/}
                        {/*    API_BASE_URL: {CHAT_CONFIG.API_BASE_URL}<br/>*/}
                        {/*    WS_BASE_URL: {CHAT_CONFIG.WS_BASE_URL}<br/>*/}
                        {/*</Typography>}*/}
                    </Paper>
                </Box>
            )}
            <ChatUserInput inputRef={inputRef}/>
        </Box>
    );
};
